/**
 * LoginPage.js - Página de Login Premium
 * CG Business ERP - Com Logo Oficial e Cores Corporativas
 * 
 * Cores Corporativas:
 * - Laranja: #F6931F
 * - Azul: #2B4DAA
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { authAPI, settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogIn, Mail, Lock, Shield, BarChart3, Users, Package, ShoppingCart, Sparkles, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';

// Feature item component
const FeatureItem = ({ icon: Icon, title, description, delay }) => (
  <div 
    className="flex items-start gap-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 transform transition-all duration-500 hover:scale-105 hover:bg-white/20"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="p-2.5 rounded-lg bg-[#F6931F]">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <h3 className="font-semibold text-white">{title}</h3>
      <p className="text-sm text-blue-100">{description}</p>
    </div>
  </div>
);

// Floating particle for background
const FloatingParticle = ({ className }) => (
  <div className={cn(
    "absolute rounded-full bg-white/10 animate-float",
    className
  )} />
);

export default function LoginPage() {
  const [loginStats, setLoginStats] = useState({ products: 308, clients: 311, modules: 25 });
  const [availableLocations, setAvailableLocations] = useState([]);
  const [identifier, setIdentifier] = useState('');
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedDocumentLocationId, setSelectedDocumentLocationId] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const { login, setDefaultDocumentLocation } = useAuth();
  const { t, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const nextRoute = useMemo(() => {
    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/')) {
      return next;
    }
    return '/dashboard';
  }, [location.search]);

  useEffect(() => {
    let active = true;

    const loadPublicLoginStats = async () => {
      try {
        const response = await authAPI.getPublicLoginStats();
        if (!active) return;

        const payload = response?.data || {};
        setLoginStats({
          products: Number(payload.products) || 0,
          clients: Number(payload.clients) || 0,
          modules: Number(payload.modules) || 0,
        });
      } catch (error) {
        console.warn('Erro ao carregar estatísticas públicas do login:', error);
      }
    };

    loadPublicLoginStats();

    return () => {
      active = false;
    };
  }, []);

  const continueWithLocation = useCallback(() => {
    const selectedLocation = availableLocations.find((loc) => loc.id === selectedDocumentLocationId);
    if (selectedLocation) {
      setDefaultDocumentLocation(
        { id: selectedLocation.id, name: selectedLocation.name },
        { persist: 'session' },
      );
    }
    setLocationPickerOpen(false);
    navigate(nextRoute);
  }, [availableLocations, navigate, nextRoute, selectedDocumentLocationId, setDefaultDocumentLocation]);

  const resolveLoginErrorMessage = useCallback((error) => {
    const statusCode = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (statusCode === 404) return t('login.userNotFound');
    if (statusCode === 401) return t('login.wrongPassword');
    if (statusCode === 500) return t('login.technicalError');
    if (typeof detail === 'string' && detail.trim()) return detail;

    return t('login.technicalError');
  }, [t]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setLoginError('');

    try {
      const response = await authAPI.login({ identifier, password });
      const accessToken = response?.data?.access_token;
      const userData = response?.data?.user;
      if (!accessToken || !userData) {
        throw new Error(t('login.technicalError'));
      }

      await login(accessToken, userData);
      if (userData.preferred_language) {
        await setLanguage(userData.preferred_language, { skipRemoteSync: true });
      }
      toast.success(t('login.welcomeToast'));

      try {
        const locationsResponse = await settingsAPI.listLocationsByType('store');
        const locations = (locationsResponse.data || []).filter((loc) => !loc.is_deleted);
        const savedDefaultLocationId = sessionStorage.getItem('default_document_location_id');
        const preferredLocationId = userData.location_id || savedDefaultLocationId;
        const preferredLocation = locations.find((loc) => loc.id === preferredLocationId);

        if (locations.length > 1) {
          const selectedLocation = preferredLocation || locations[0];
          setAvailableLocations(locations);
          setSelectedDocumentLocationId(selectedLocation?.id || '');
          setDefaultDocumentLocation(
            { id: selectedLocation.id, name: selectedLocation.name },
            { persist: 'session' },
          );
          navigate(nextRoute);
          return;
        }

        if (locations[0]) {
          setDefaultDocumentLocation(
            { id: locations[0].id, name: locations[0].name },
            { persist: 'session' },
          );
          navigate(nextRoute);
          return;
        }

        if (userData.location_id) {
          setDefaultDocumentLocation(
            {
              id: userData.location_id,
              name: userData.location_name,
            },
            { persist: 'session' },
          );
        }

        navigate(nextRoute);
      } catch (error) {
        console.error('Error loading login locations:', error);
        navigate(nextRoute);
      }
    } catch (error) {
      const message = resolveLoginErrorMessage(error);
      setLoginError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [identifier, loading, login, navigate, nextRoute, password, resolveLoginErrorMessage, setDefaultDocumentLocation, setLanguage, t]);

  return (
    <div className="flex min-h-screen overflow-hidden">
      {/* Left Side - Login Form */}
      <div className="flex flex-1 flex-col justify-center px-6 py-12 lg:px-12 xl:px-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="mx-auto w-full max-w-md">
          {/* Logo & Title */}
          <div className="mb-10 text-center lg:text-left">
            <div className="flex flex-col items-center lg:items-start gap-4 mb-4">
              {/* Official Logo */}
              <img 
                src="/images/logo-cg.png" 
                alt="CG Business Logo" 
                className="h-24 w-auto object-contain"
                data-testid="login-logo"
              />
              <div>
                <p className="text-sm text-[#2B4DAA] font-medium">
                  {t('login.systemLabel')}
                </p>
              </div>
            </div>
            <p className="text-slate-500 mt-2">
              {t('login.accessPanel')}
            </p>
          </div>

          {/* Login Card */}
          <Card className="border-0 shadow-2xl shadow-blue-200/50 bg-white/90 backdrop-blur-xl">
            <CardContent className="pt-8 pb-8 px-8">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">{t('login.signInTitle')}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('login.signInDescription')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                {loginError ? (
                  <div
                    className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700"
                    role="alert"
                    data-testid="login-error-alert"
                  >
                    {loginError}
                  </div>
                ) : null}

                {/* Identifier Field */}
                <div className="space-y-2">
                  <Label htmlFor="identifier" className="text-slate-700">{t('login.identifierLabel')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="identifier"
                      type="text"
                      placeholder={t('login.identifierPlaceholder')}
                      value={identifier}
                      onChange={(e) => {
                        setIdentifier(e.target.value);
                        if (loginError) setLoginError('');
                      }}
                      autoComplete="username"
                      required
                      className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2B4DAA] transition-colors"
                      data-testid="login-identifier-input"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">{t('labels.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (loginError) setLoginError('');
                      }}
                      autoComplete="current-password"
                      required
                      className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2B4DAA] transition-colors"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm"
                      data-testid="password-visibility-toggle"
                    >
                      {showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-sm font-medium text-[#2B4DAA] transition-colors hover:text-[#1E3A8A]"
                    data-testid="login-forgot-password-button"
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>

                {/* Submit Button - Corporate Orange */}
                <Button
                  type="submit"
                  className={cn(
                    "w-full h-12 text-base font-medium transition-all duration-300",
                    "bg-gradient-to-r from-[#F6931F] to-[#E8850F]",
                    "hover:from-[#E8850F] hover:to-[#D4770A]",
                    "shadow-lg shadow-orange-200 hover:shadow-xl hover:shadow-orange-300",
                    "active:scale-[0.98]"
                  )}
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>{t('login.signingIn')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <LogIn className="h-5 w-5" />
                      <span>{t('login.signInButton')}</span>
                    </div>
                  )}
                </Button>
              </form>

            </CardContent>
          </Card>

          {/* Footer with Creator Credits */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              © 2026 CG Business LDA • Maputo, Moçambique
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              Desenvolvido por <span className="font-medium text-[#2B4DAA]">Charles Gabell</span> • Fundador & CEO
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Feature Showcase with Corporate Blue */}
      <div className="relative hidden lg:flex lg:flex-1 flex-col justify-center px-12 xl:px-20 bg-gradient-to-br from-[#2B4DAA] via-[#1E3A8A] to-[#1E2F5A] overflow-hidden">
        {/* Background Particles */}
        <FloatingParticle className="w-32 h-32 top-20 left-10 animate-pulse" />
        <FloatingParticle className="w-24 h-24 top-40 right-20 animate-bounce" />
        <FloatingParticle className="w-16 h-16 bottom-32 left-1/4" />
        <FloatingParticle className="w-20 h-20 bottom-20 right-1/3 animate-pulse" />
        
        {/* Decorative Orange Accent */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F6931F]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#F6931F]/10 rounded-full blur-2xl" />
        
        {/* Content */}
        <div className="relative z-10 max-w-lg">
          {/* Logo on dark side */}
          <div className="mb-8">
            <img 
              src="/images/logo-cg.png" 
              alt="CG Business" 
              className="h-20 w-auto brightness-110"
            />
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('login.headline')}
          </h2>
          <p className="text-lg text-blue-100 mb-10">
            {t('login.subheadline')}
          </p>

          {/* Features */}
          <div className="space-y-4">
            <FeatureItem 
              icon={ShoppingCart} 
              title={t('login.featurePosTitle')}
              description={t('login.featurePosDescription')}
              delay={100}
            />
            <FeatureItem 
              icon={Package} 
              title={t('login.featureInventoryTitle')}
              description={t('login.featureInventoryDescription', { count: loginStats.products || 0 })}
              delay={200}
            />
            <FeatureItem 
              icon={Users} 
              title={t('login.featureCrmTitle')}
              description={t('login.featureCrmDescription', { count: loginStats.clients || 0 })}
              delay={300}
            />
            <FeatureItem 
              icon={BarChart3} 
              title={t('login.featureAnalyticsTitle')}
              description={t('login.featureAnalyticsDescription')}
              delay={400}
            />
          </div>

          {/* Stats with Orange accent */}
          <div className="mt-12 flex items-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#F6931F]">{loginStats.modules || 0}+</p>
              <p className="text-sm text-blue-200">{t('login.modulesLabel')}</p>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-bold text-[#F6931F]">16%</p>
              <p className="text-sm text-blue-200">{t('login.vatLabel')}</p>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-bold text-[#F6931F]">24/7</p>
              <p className="text-sm text-blue-200">{t('login.operationalLabel')}</p>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-10 flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
              <CheckCircle className="h-4 w-4 text-[#F6931F]" />
              <span className="text-xs text-white">{t('login.secureSsl')}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
              <Shield className="h-4 w-4 text-[#F6931F]" />
              <span className="text-xs text-white">{t('login.protectedData')}</span>
            </div>
          </div>
        </div>

        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E2F5A]/50 to-transparent pointer-events-none" />
      </div>

      <Dialog open={locationPickerOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="login-document-location-dialog">
          <DialogHeader>
            <DialogTitle>{t('login.locationDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('login.locationDialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="document-location-select">{t('login.locationDialogLabel')}</Label>
            <Select value={selectedDocumentLocationId} onValueChange={setSelectedDocumentLocationId}>
              <SelectTrigger id="document-location-select" data-testid="login-document-location-select">
                <SelectValue placeholder={t('login.locationDialogPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {availableLocations.map((locationOption) => (
                  <SelectItem key={locationOption.id} value={locationOption.id} data-testid={`login-document-location-option-${locationOption.id}`}>
                    {locationOption.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              onClick={continueWithLocation}
              disabled={!selectedDocumentLocationId}
              data-testid="login-document-location-confirm-button"
            >
              {t('login.continueButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ForgotPasswordDialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen} />

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
